import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import type { JobIntelUserProfile } from '@/types/jobintelProfile';
import {
  getProfile,
  upsertProfile,
  calculateCompletionPercentage,
} from '@/services/jobintelProfileService';

export function useJobIntelProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<JobIntelUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await getProfile(user.id);
      setProfile(data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const saveProfile = useCallback(async (updates: Partial<JobIntelUserProfile>) => {
    if (!user?.id) return null;
    setSaving(true);
    try {
      const result = await upsertProfile({ ...updates, user_id: user.id });
      setProfile(result);
      toast({ title: 'Profile saved', description: 'Your career profile has been updated.' });
      return result;
    } catch (err: any) {
      toast({ title: 'Error saving profile', description: err.message, variant: 'destructive' });
      return null;
    } finally {
      setSaving(false);
    }
  }, [user?.id, toast]);

  const completionPercentage = profile
    ? calculateCompletionPercentage(profile)
    : 0;

  return {
    profile,
    loading,
    saving,
    saveProfile,
    fetchProfile,
    completionPercentage,
    hasProfile: !!profile,
  };
}
